import React, { FC, useMemo, useEffect, useState } from 'react'
import { Select, Input, Button, TreeSelect, DatePicker, Cascader } from 'antd'
import type { DefaultOptionType } from 'antd/lib/select'
import styled from 'styled-components'
import { useDebounceFn } from 'ahooks'
import dayjs from '@/utils/dayjs'
import { t } from '@/i18n'

const { Option } = Select
const { RangePicker } = DatePicker
const { SHOW_CHILD } = Cascader

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;

  .filter-container {
    display: flex;
    align-items: center;
  }

  .field-select {
    width: 120px;
    .ant-select-selector {
      height: 32px !important;
      border-right: none;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    .ant-select-selection-item {
      line-height: 30px !important;
    }
  }

  .ant-select-dropdown {
    z-index: 1050 !important;
  }

  .value-input,
  .value-select {
    width: 215px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    .ant-select-selector,
    &.ant-input {
      height: 32px !important;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    .ant-select-selection-item {
      line-height: 30px !important;
    }
    &.ant-input {
      line-height: 32px !important;
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  /* RangePicker 样式 */
  .value-select.ant-picker-range {
    width: 360px;
    height: 32px !important;
    .ant-picker-input > input {
      font-size: 14px;
    }
  }

  /* 确保第一个选择框的箭头显示 */
  .field-select .ant-select-arrow {
    right: 11px;
  }

  /* 确保第二个选择框的箭头显示 */
  .value-select .ant-select-arrow {
    right: 11px;
  }

  /* 处理选择框hover时的边框叠加效果 */
  .field-select:hover + .value-input,
  .field-select:hover + .value-select {
    border-left-color: #40a9ff;
  }

  .field-select.ant-select-focused + .value-input,
  .field-select.ant-select-focused + .value-select {
    border-left-color: #40a9ff;
  }

  /* 统一按钮高度 */
  .reset-button {
    margin-left: 8px;
    height: 32px;
    padding: 4px 15px;
    line-height: 24px;
  }

  /* 处理多选状态下的高度 */
  .value-select.ant-select-multiple .ant-select-selector {
    height: auto !important;
    min-height: 32px !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    .ant-select-selection-item {
      height: 24px;
      line-height: 22px !important;
      margin-top: 2px;
      margin-bottom: 2px;
    }
  }
`

export interface FilterOption {
  label: string
  value: string | number
  type: 'select' | 'input' | 'treeSelect' | 'cascader' | 'dateRange'
  placeholder?: string
  options?: { label: string; value: string | number }[]
  mode?: 'multiple' | 'tags'
}

interface FilterFormSelectProps {
  filterOptions: FilterOption[]
  value?: {
    field: string | number
    value:
      | string
      | number
      | string[]
      | number[]
      | { tacticId?: string; technologyId?: string; subTechnologyId?: string }[]
      | undefined
  }
  onChange?: (value: {
    field: string | number
    value:
      | string
      | number
      | string[]
      | number[]
      | { tacticId?: string; technologyId?: string; subTechnologyId?: string }[]
      | undefined
  }) => void
  style?: React.CSSProperties
  className?: string
  showReset?: boolean
}

export const FilterFormSelect: FC<FilterFormSelectProps> = ({
  filterOptions,
  value,
  onChange,
  style,
  className,
  showReset = false,
}) => {
  const selectedOption = useMemo(
    () => filterOptions?.find(option => option.value === value?.field),
    [filterOptions, value?.field],
  )

  // 添加本地状态来管理输入框的值
  const [inputValue, setInputValue] = useState((value?.value as string) || '')

  // 当外部value变化时，更新本地状态
  useEffect(() => {
    setInputValue((value?.value as string) || '')
  }, [value?.value])

  // 添加防抖函数
  const { run: debounceOnChange } = useDebounceFn(
    newValue => {
      onChange?.(newValue)
    },
    {
      wait: 500,
    },
  )

  // 初始化默认值
  useEffect(() => {
    if (filterOptions?.length > 0 && !value?.field) {
      const firstOption = filterOptions[0]
      const defaultValue =
        firstOption.type === 'select'
          ? firstOption.mode === 'multiple'
            ? []
            : ''
          : firstOption.type === 'dateRange'
          ? []
          : ''

      onChange?.({
        field: firstOption.value,
        value: defaultValue,
      })
    }
  }, [filterOptions])

  const handleFieldChange = (fieldValue: string | number) => {
    const option = filterOptions.find(opt => opt.value === fieldValue)
    // 切换字段时，完全重置状态
    setInputValue('')
    onChange?.({
      field: fieldValue,
      value:
        option?.type === 'select' || option?.type === 'cascader'
          ? option?.mode === 'multiple'
            ? []
            : undefined
          : option?.type === 'dateRange'
          ? []
          : undefined,
    })
  }

  // 处理值变化
  const handleValueChange = (val: string | number | string[] | number[], selectedOptions?: any) => {
    const newValue = {
      field: value?.field || '',
      value: val,
    }

    console.log('selectedOptions', selectedOptions)

    // 如果是输入框类型，使用防抖
    if (selectedOption?.type === 'input') {
      // 立即更新本地状态
      setInputValue(val as string)
      // 防抖触发搜索
      debounceOnChange(newValue)
    } else if (selectedOption?.type === 'cascader') {
      const selectValue: { tacticId?: string; technologyId?: string; subTechnologyId?: string }[] =
        []
      if (val instanceof Array) {
        val.forEach(item => {
          const obj: { tacticId?: string; technologyId?: string; subTechnologyId?: string } = {}
          if (item[0]) obj.tacticId = item[0]
          if (item[1]) obj.technologyId = item[1]
          if (item[2]) obj.subTechnologyId = item[2]
          selectValue.push(obj)
        })
      }
      const fValue = {
        field: value?.field || '',
        value: selectValue,
      }
      onChange?.(fValue)
    } else if (value?.field === 'hostIds') {
      const raw = selectedOptions?.raw
      const fValue = {
        field: value?.field || '',
        value: val,
        raw: raw,
      }
      onChange?.(fValue)
    } else {
      console.log('newValue', newValue)
      onChange?.(newValue)
    }
  }

  const handleReset = () => {
    if (filterOptions?.length > 0) {
      const firstOption = filterOptions[0]
      const defaultValue =
        firstOption.type === 'select'
          ? firstOption.mode === 'multiple'
            ? []
            : ''
          : firstOption.type === 'dateRange'
          ? []
          : ''

      onChange?.({
        field: firstOption.value,
        value: defaultValue,
      })
    }
  }

  const getDefaultPlaceholder = (type: 'select' | 'input') =>
    type === 'input' ? t('请输入') : t('请选择')

  const filter = (v: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(v.toLowerCase()) > -1)

  // 时间范围快捷选项（antd 4.0 使用 ranges 属性）
  const rangePresets = {
    [t('今天')]: [dayjs().startOf('day'), dayjs().endOf('day')],
    [t('最近7天')]: [dayjs().subtract(6, 'days').startOf('day'), dayjs().endOf('day')],
    [t('最近30天')]: [dayjs().subtract(29, 'days').startOf('day'), dayjs().endOf('day')],
    [t('本月')]: [dayjs().startOf('month'), dayjs().endOf('month')],
    [t('本年')]: [dayjs().startOf('year'), dayjs().endOf('year')],
  }

  // 处理时间范围变化
  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const startTime = dayjs(dates[0]).format('YYYY-MM-DD HH:mm:ss')
      const endTime = dayjs(dates[1]).format('YYYY-MM-DD HH:mm:ss')

      // 返回标准的 { field, value: [start_time, end_time] } 格式
      onChange?.({
        field: value?.field || '',
        value: [startTime, endTime],
      })
    } else {
      // 清空时：返回空数组
      onChange?.({
        field: value?.field || '',
        value: [],
      })
    }
  }

  return (
    <FilterWrapper style={style} className={className}>
      <div className="filter-container">
        <Select
          className="field-select"
          value={value?.field}
          onChange={handleFieldChange}
          placeholder={t('请选择筛选条件')}
          showSearch
          optionFilterProp="children"
          filterOption={(input: string, option?: DefaultOptionType) =>
            (option?.children || '').toString().toLowerCase().includes(input.toLowerCase())
          }
          showArrow
          maxTagCount={2}
        >
          {filterOptions?.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        {selectedOption?.type === 'input' ? (
          <Input
            className="value-input"
            placeholder={
              selectedOption.placeholder ||
              `${getDefaultPlaceholder('input')}${selectedOption.label}`
            }
            value={inputValue}
            onChange={e => handleValueChange(e.target.value)}
            allowClear
          />
        ) : selectedOption?.type === 'select' ? (
          <Select
            className="value-select"
            mode={selectedOption.mode}
            placeholder={
              selectedOption.placeholder ||
              `${getDefaultPlaceholder('select')}${selectedOption.label}`
            }
            maxTagCount={selectedOption.mode === 'multiple' ? 2 : undefined}
            maxTagTextLength={selectedOption.mode === 'multiple' ? 2 : undefined}
            value={value?.value}
            onChange={(v, selectedOptions) => handleValueChange(v, selectedOptions)}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const searchText = input.toLowerCase()
              const label = option?.label?.toString() || ''
              const optionValue = option?.value?.toString() || ''
              // 同时搜索 label 和 value 字段，不区分大小写
              return (
                label.toLowerCase().includes(searchText) ||
                optionValue.toLowerCase().includes(searchText)
              )
            }}
            options={selectedOption.options}
          />
        ) : selectedOption?.type === 'treeSelect' ? (
          <TreeSelect
            className="value-select"
            maxTagCount={'responsive'}
            maxTagTextLength={10}
            multiple
            treeCheckable
            placeholder={
              selectedOption.placeholder ||
              `${getDefaultPlaceholder('select')}${selectedOption.label}`
            }
            value={value?.value || []}
            onChange={handleValueChange}
            treeData={selectedOption.options}
            allowClear
          />
        ) : selectedOption?.type === 'cascader' ? (
          <Cascader
            className="value-select"
            options={selectedOption.options}
            onChange={v => handleValueChange(v as unknown as string[] | number[])}
            placeholder={
              selectedOption.placeholder ||
              `${getDefaultPlaceholder('select')}${selectedOption.label}`
            }
            value={
              Array.isArray(value?.value) &&
              value.value.length > 0 &&
              typeof value.value[0] === 'object'
                ? value.value.map((item: any) =>
                    [item.tacticId, item.technologyId, item.subTechnologyId].filter(Boolean),
                  )
                : (value?.value as string[] | number[]) || []
            }
            showSearch={{ filter }}
            multiple
            maxTagCount="responsive"
            onSearch={v => console.log(v)}
            showCheckedStrategy={SHOW_CHILD}
          />
        ) : selectedOption?.type === 'dateRange' ? (
          <RangePicker
            className="value-select"
            placeholder={
              selectedOption.placeholder
                ? [selectedOption.placeholder, selectedOption.placeholder]
                : [t('开始时间'), t('结束时间')]
            }
            showTime={{ format: 'HH:mm:ss' }}
            format="YYYY-MM-DD HH:mm:ss"
            value={
              Array.isArray(value?.value) && value.value.length === 2
                ? [dayjs(value.value[0] as string), dayjs(value.value[1] as string)]
                : null
            }
            onChange={handleDateRangeChange}
            ranges={rangePresets as any}
            allowClear
          />
        ) : null}
      </div>

      {showReset && (
        <Button className="reset-button" onClick={handleReset}>
          {t('重置')}
        </Button>
      )}
    </FilterWrapper>
  )
}
